import { RootStore } from "@intrasync/stores";
import type { RootStoreOptions } from "@intrasync/stores";
import { registerAiAgentClientServices } from "@intrasync/ai-agent";
import apiClient from "./api-client/apiClient";
import rootStoreConfig from "./rootStoreConfig";

interface Window {
  rootStore?: RootStore;
  dateFNS?: unknown;
  __STORE_COUNT__?: number;
  __MEMORY_MONITOR__?: unknown;
}

type ApiClientMode = Parameters<typeof apiClient>[0];

/**
 * Build the Feathers `app` the published @intrasync/stores RootStore requires.
 *
 * The library intentionally no longer constructs its own Feathers client (that
 * logic was lifted out so the package stays app-agnostic); its constructor
 * throws unless an `app` is injected via `props.app`. We build it here with the
 * app-local apiClient, mirroring the auth-storage key derivation the RootStore
 * performs internally so socket auth reads/writes the same keys.
 */
function buildApp(config: RootStoreOptions) {
  const mode = (config.mode ?? "dev") as ApiClientMode;
  const isProdMode = ["production", "prod", "live"].includes(mode);
  const authStorageKey = `feathers-jwt${isProdMode ? "" : `-${mode}`}`;
  const refreshStorageKey = `refresh-token${isProdMode ? "" : `-${mode}`}`;

  return apiClient(mode, {
    ...(config.apiOptions as object),
    authStorageKey,
    refreshStorageKey,
  });
}

export function createRootStore(): RootStore {
  const app = buildApp(rootStoreConfig);
  // Registers every ai-agent custom Feathers method on the socket client
  // (runs.start/cancel, ingestion-jobs.process/retry, retrieval.query, …).
  // Must run before any store resolves those services.
  registerAiAgentClientServices(app);
  const rootStore = new RootStore({
    ...rootStoreConfig,
    app,
    // Tenant key module packages (AgentHub) scope records by. First-class in
    // @intrasync/stores 26.7.16 — a constructor prop + `setCompanyId` action;
    // `rootStore.companyId` is now a getter, so the old direct assignment
    // would throw. This deployment is single-tenant.
    companyId: "demo",
  });

  // AgentHub host contract: since @intrasync/ai-agent@0.0.17,
  // getAiAgentHostUser falls back to authStore.user/currentUser natively and
  // DefaultPermissions honors the admin signal even with no user object — the
  // former Object.defineProperty(rootStore, "user") shim is no longer needed.

  // if (import.meta.env.MODE === "development") {
  (window as Window).rootStore = rootStore;
  // }

  return rootStore;
}
